import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import type * as C from "@zooi/contracts";
import { api, ApiError, q } from "../api.ts";
import { AuthArrow, AuthFrame } from "../auth-frame.tsx";
import { DETAIL_LABELS, SETTINGS_LABELS, WORKSPACE_LABELS as L } from "../labels.ts";
import { dateTime, list } from "../format.ts";
import { Button, ErrorText } from "../ui.tsx";
import { PropertyList, PropertyRow } from "../ui/props.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { setWorkspaceScope } from "../workspace-scope.ts";

// Accepting an invitation (docs/design-system.md §0.4h, plan M7 step 7j):
// outside the shell, one card. Without a session the person signs in first
// and comes back to this invitation.

const D = DETAIL_LABELS.invite;

export function Invite() {
  const { invitationId } = useParams({ strict: false }) as { invitationId: string };
  const navigate = useNavigate();
  const me = useQuery({ ...q.me, retry: false });
  const signedOut = me.error instanceof ApiError && me.error.status === 401;
  const invitation = useQuery({ queryKey: ["invitation", invitationId], queryFn: () => api.get<C.InvitationDetail>(`/api/invitations/${encodeURIComponent(invitationId)}`), retry: false, enabled: me.isSuccess });
  const accept = useMutation({
    mutationFn: () => api.post<C.Workspace>(`/api/invitations/${encodeURIComponent(invitationId)}/accept`),
    onSuccess: (workspace) => { setWorkspaceScope(workspace.id); window.location.replace("/"); },
  });
  useEffect(() => {
    // Sign in first; the sign-in page follows `next` back here (a path on this site only).
    if (signedOut) window.location.replace(`/sign-in?next=${encodeURIComponent(`/invite/${invitationId}`)}`);
  }, [signedOut, invitationId]);

  if (me.isLoading || signedOut || invitation.isLoading) return <AuthFrame><Skeleton shape="text" /></AuthFrame>;
  const data = invitation.data;
  const name = data?.workspace.name ?? D.fallback;
  return <AuthFrame>
    {data?.status === "pending" ? <>
      <h1>{D.join(name)}</h1>
      <p className="z-muted">{L.invitationScope}</p>
      <PropertyList>
        <PropertyRow label={D.email}>{data.email}</PropertyRow>
        <PropertyRow label={D.inviter}>{data.inviter}</PropertyRow>
        <PropertyRow label={L.role}>{SETTINGS_LABELS.role[data.role]}</PropertyRow>
        <PropertyRow label={L.access}>{data.product_ids === null ? L.all : data.products.length ? list(data.products.map((product) => product.name)) : L.none}</PropertyRow>
        <PropertyRow label={L.expires}>{dateTime(data.expires_at)}</PropertyRow>
      </PropertyList>
      <div className="z-auth-actions">
        <Button tone="primary" busy={accept.isPending} onClick={() => accept.mutate()}>{D.accept} <AuthArrow /></Button>
      </div>
    </> : <>
      <h1>{D.closed}</h1>
      <p className="z-muted">{D.closedNote}</p>
      <div className="z-auth-actions"><Button onClick={() => void navigate({ to: "/" })}>{D.return}</Button></div>
    </>}
    {/* An invitation that cannot be read is said above; only accepting can fail here. */}
    <ErrorText error={accept.error ?? invitation.error ?? (signedOut ? null : me.error)} />
  </AuthFrame>;
}
